import React, {
    JSX, useEffect, useMemo,
} from 'react';
import Modal from "react-bootstrap/Modal";
import Button from 'react-bootstrap/Button';
import { IoMdClose } from "react-icons/io";
import { useDispatch } from 'react-redux';
import { addItemsToCart } from 'app/store/slices/orderSlice';
import {useGetAllProductsQuery, useGetSuggestedProductsQuery} from 'app/services/api/fetchGateApi';
import { flattenMenuProducts } from 'app/shared/utils/OrderHelper';
import { setHasSeenUpsellModal } from 'app/store/slices/authSlice';
import ModalLoader from 'app/shared/components/ModalLoader';
import { useGetUpsellsQuery } from 'store/services/tOrderApi';
import { useAppSelector } from 'store/hooks';
import { updateCart } from 'store/slices/tOrderSlice';
import {
    getAvailableUpsells,
    getUpsellImage,
    mapUpsellToOrderItem,
} from 'store/slices/orderSliceUtil';
import { Upsell } from 'store/slices/tOrderTypes';
import useGetPlatformInfo from 'hooks/useGetPlatformInfo';
import { ProductType } from '../types/OrderTypes';
import UpsellItem from './UpsellItem';

type Props = {
    showUpsellModal: boolean;
    onUpsellModalClose: (shouldOpenCart: boolean) => void;
    storeNumber: string;
    isToastStore: boolean;
};
const UpsellModal: React.FC<Props> = (
    {
        showUpsellModal,
        onUpsellModalClose,
        storeNumber,
        isToastStore,
    },
):
JSX.Element | null => {
    const { isShellApp } = useGetPlatformInfo();
    const {
        data: legacyUpsellMenuIds,
        error: legacyUpsellError,
        isLoading: isLegacyUpsellLoading,
    } = useGetSuggestedProductsQuery('', { skip: isToastStore });
    const {
        data: legacyMenu,
        error: legacyMenuError,
        isLoading: isLegacyMenuLoading,
    } = useGetAllProductsQuery(storeNumber, {skip: isToastStore || storeNumber === ""});
    const restaurantGuid = useAppSelector((state) => (
        state.stores.selectedStore?.c_toastRestaurantId
        || state.stores.tRestaurantId
        || state.tOrders.storeId
    ));
    const toastMenu = useAppSelector((state) => state.tOrders.menu);
    const normalizedRestaurantGuid = restaurantGuid?.trim().toLowerCase();
    const normalizedMenuRestaurantGuid = toastMenu?.restaurantGuid.trim().toLowerCase();
    const hasCurrentToastMenu = Boolean(
        normalizedRestaurantGuid
        && normalizedMenuRestaurantGuid === normalizedRestaurantGuid,
    );
    const restaurantGroupId = hasCurrentToastMenu
        ? toastMenu?.restaurantGroupId
        : undefined;
    const {
        data: toastUpsellData,
        error: toastUpsellError,
        isLoading: isToastUpsellLoading,
    } = useGetUpsellsQuery(restaurantGroupId || 0, {
        skip: !isToastStore || !restaurantGroupId,
    });
    const dispatch = useDispatch();
    const legacyUpsellProducts = useMemo(() => {
        const productsMenu = flattenMenuProducts(legacyMenu?.menu || []);
        const upsellIds = (legacyUpsellMenuIds?.data || []);
        const usedIds = new Set();
        return upsellIds.reduce<ProductType[]>((acc, productId) => {
            productsMenu.forEach((product: ProductType) => {
                product?.omsData?.salesItems?.forEach((salesItem) => {
                    if (productId === Number(salesItem.productId) && !usedIds.has(productId)) {
                        usedIds.add(productId);
                        acc.push({
                            ...product,
                            productSizeId: salesItem?.productId,
                            currentPrice: salesItem?.currentPrice || 0.00,
                            displayProductSize: salesItem?.name,
                            subProducts: [],
                        });
                    }
                });
            });
            return acc;
        }, []);
    }, [legacyMenu?.menu, legacyUpsellMenuIds]);

    const toastUpsells = useMemo(
        () => getAvailableUpsells(toastUpsellData?.upsells || []),
        [toastUpsellData?.upsells],
    );
    const toastUpsellImages = useMemo(() => new Map(
        toastUpsells.map((item) => [
            item.multiLocationId,
            getUpsellImage(item, toastMenu),
        ]),
    ), [toastMenu, toastUpsells]);

    const handleLegacyAddToCart = (clickedItem: ProductType) => {
        dispatch(addItemsToCart(clickedItem));
    };

    const handleToastAddToCart = (clickedItem: Upsell) => {
        if (!toastUpsellData) return;
        const mappedOrderItem = mapUpsellToOrderItem(
            clickedItem,
            toastUpsellData.menuGroupMultiLocationId,
        );
        mappedOrderItem.imageThumb = getUpsellImage(clickedItem, toastMenu);
        dispatch(updateCart(mappedOrderItem));
    };

    const handleModalClose = () => {
        dispatch(setHasSeenUpsellModal(true));
        onUpsellModalClose(true);
    };

    const error = isToastStore
        ? toastUpsellError
        : legacyUpsellError || legacyMenuError;
    const isLoading = isToastStore
        ? isToastUpsellLoading
        : isLegacyUpsellLoading || isLegacyMenuLoading;
    const hasResolvedWithNoItems = !error && (isToastStore
        ? Boolean(toastUpsellData && toastUpsells.length === 0)
        : Boolean(
            legacyMenu?.menu
            && legacyUpsellMenuIds?.data
            && legacyUpsellProducts.length === 0,
        ));
    const isToastContextMissing = Boolean(isToastStore && !restaurantGroupId);
    const shouldSkipModal = Boolean(error)
        || hasResolvedWithNoItems
        || isToastContextMissing;

    useEffect(() => {
        if (!showUpsellModal || !shouldSkipModal) return;
        if (hasResolvedWithNoItems) {
            dispatch(setHasSeenUpsellModal(true));
        }
        onUpsellModalClose(true);
    }, [
        dispatch,
        hasResolvedWithNoItems,
        onUpsellModalClose,
        shouldSkipModal,
        showUpsellModal,
    ]);

    if (showUpsellModal && isLoading) {
        return (<ModalLoader size="sm" showModal={isLoading} />);
    }

    if (shouldSkipModal) return null;

    return (
        <Modal
            show={showUpsellModal}
            backdrop={isShellApp ? 'static' : true}
            className="d-flex align-items-end"
            onHide={handleModalClose}
            contentClassName="upsell-modal-content"
            dialogClassName="upsell-modal-dialog"
        >
            <Modal.Header>
            <Button
                aria-label="close"
                onClick={handleModalClose}
                className="upsell-btn-close"
            >
                <IoMdClose/>
            </Button>
            </Modal.Header>
            <Modal.Body className="upsell-modal">
                <div className="upsell-modal-header">
                    You May Also Like...
                </div>
                <div className="upsell-items">
                    {isToastStore ? toastUpsells.map((item) => (
                        <UpsellItem
                            key={`${item.multiLocationId}-${item.modifierOptionMultiLocationId || item.size || ''}`}
                            title={`${item.name}${item.size ? ` - ${item.size}` : ''}`}
                            image={toastUpsellImages.get(item.multiLocationId)}
                            price={item.price}
                            addToCart={() => handleToastAddToCart(item)}
                        />
                    )) : legacyUpsellProducts.map((item, index) => (
                        <UpsellItem
                            key={`${item.omsData.salesItems[0].productId}${item.productSizeId}${index.toString()}`}
                            title={item.title}
                            image={item.imageThumb}
                            price={item.currentPrice || 0}
                            addToCart={() => handleLegacyAddToCart(item)}
                        />
                    ))}
                </div>
                <div className="upsell-modal-footer">
                    <Button
                        className="order-now-card-button"
                        onClick={handleModalClose}
                        aria-label="go to cart"
                    >
                        GO TO CART
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default UpsellModal;
